import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { Artifact } from '@trustero/trustero-api-web/lib/artifact/artifact_pb'
import { ChatMessage } from '@trustero/trustero-api-web/lib/chat/chat_pb'
import { Playbook } from '@trustero/trustero-api-web/lib/playbook/playbook_pb'
import { OnSend } from '../conversation/chatConversation.hooks'
import { PlaybookPanelMode } from '../playbooks/CreatePlaybookSidePanel'

export type ExpandType = 'all' | 'success' | 'failure' | 'partial'

export type ArtifactPanelState = {
  artifactPanelOpen: boolean
  setArtifactPanelOpen: (artifactPanelOpen: boolean) => void
  selectedArtifact: Artifact | null
  setSelectedArtifact: (selectedArtifact: Artifact | null) => void
  disabled: boolean
  setDisabled: (disabled: boolean) => void
  message: ChatMessage | null
  setMessage: (message: ChatMessage | null) => void
  nextUserMessage: ChatMessage | null
  setNextUserMessage: (nextUserMessage: ChatMessage | null) => void
  onSend: OnSend | null
  setOnSend: (onSend: OnSend | null) => void
  allArtifacts: Artifact[]
  setAllArtifacts: (allArtifacts: Artifact[]) => void
  agentBusy: boolean | null
  setAgentBusy: (agentBusy: boolean | null) => void
  printComponent: React.RefObject<HTMLDivElement> | null
  setPrintComponent: (
    printComponent: React.RefObject<HTMLDivElement> | null,
  ) => void
  accordionKeys: string[]
  setAccordionKeys: React.Dispatch<React.SetStateAction<string[]>>
  expandSections: boolean
  setExpandSections: React.Dispatch<React.SetStateAction<boolean>>
  expandAnswers: boolean
  setExpandAnswers: React.Dispatch<React.SetStateAction<boolean>>
  expandType: ExpandType
  setExpandType: React.Dispatch<React.SetStateAction<ExpandType>>
}

const initialArtifactPanelState: ArtifactPanelState = {
  artifactPanelOpen: false,
  setArtifactPanelOpen: () => {
    return
  },
  selectedArtifact: null,
  setSelectedArtifact: () => {
    return
  },
  disabled: false,
  setDisabled: () => {
    return
  },
  message: null,
  setMessage: () => {
    return
  },
  nextUserMessage: null,
  setNextUserMessage: () => {
    return
  },
  allArtifacts: [],
  setAllArtifacts: () => {
    return
  },
  onSend: null,
  setOnSend: () => {
    return
  },
  agentBusy: null,
  setAgentBusy: () => {
    return
  },
  printComponent: null,
  setPrintComponent: () => {
    return
  },
  accordionKeys: [],
  setAccordionKeys: () => {
    return
  },
  expandSections: false,
  setExpandSections: () => {
    return
  },
  expandAnswers: false,
  setExpandAnswers: () => {
    return
  },
  expandType: 'all',
  setExpandType: () => {
    return
  },
}

const ArtifactPanelContext = createContext<ArtifactPanelState>(
  initialArtifactPanelState,
)

export const useArtifactPanelContext = (): ArtifactPanelState =>
  useContext(ArtifactPanelContext)

export const ArtifactPanelProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [artifactPanelOpen, setArtifactPanelOpen] = useState(false)
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(
    null,
  )
  const [disabled, setDisabled] = useState(false)
  const [message, setMessage] = useState<ChatMessage | null>(null)
  const [nextUserMessage, setNextUserMessage] = useState<ChatMessage | null>(
    null,
  )
  const [onSend, setOnSend] = useState<OnSend | null>(null)
  const [allArtifacts, setAllArtifacts] = useState<Artifact[]>([])
  const [agentBusy, setAgentBusy] = useState<boolean | null>(null)
  const [printComponent, setPrintComponent] =
    useState<React.RefObject<HTMLDivElement> | null>(null)

  const [accordionKeys, setAccordionKeys] = useState<string[]>([])
  const [expandSections, setExpandSections] = useState<boolean>(false)
  const [expandAnswers, setExpandAnswers] = useState<boolean>(false)
  const [expandType, setExpandType] = useState<ExpandType>('all')

  useEffect(() => {
    if (selectedArtifact) {
      const latestArtifact = allArtifacts.find(
        (a) => a.getId() === selectedArtifact.getId(),
      )
      if (
        latestArtifact &&
        latestArtifact?.getUpdatedAt() > selectedArtifact.getUpdatedAt()
      ) {
        setSelectedArtifact(latestArtifact)
      }
    }
  }, [selectedArtifact, allArtifacts])

  const artifactId = useMemo(
    () => selectedArtifact?.getId(),
    [selectedArtifact],
  )

  useEffect(() => {
    setAccordionKeys([])
    setExpandSections(false)
    setExpandAnswers(false)
    setExpandType('all')
  }, [artifactId])

  return (
    <ArtifactPanelContext.Provider
      value={{
        artifactPanelOpen,
        setArtifactPanelOpen,
        selectedArtifact,
        setSelectedArtifact,
        disabled,
        setDisabled,
        message,
        setMessage,
        nextUserMessage,
        setNextUserMessage,
        onSend,
        setOnSend,
        allArtifacts,
        setAllArtifacts,
        agentBusy,
        setAgentBusy,
        printComponent,
        setPrintComponent,
        accordionKeys,
        setAccordionKeys,
        expandSections,
        setExpandSections,
        expandAnswers,
        setExpandAnswers,
        expandType,
        setExpandType,
      }}
    >
      {children}
    </ArtifactPanelContext.Provider>
  )
}

// Playbook Panel Context
export type PlaybookPanelState = {
  playbookPanelOpen: boolean
  setPlaybookPanelOpen: (open: boolean) => void
  selectedPlaybook: Playbook | null
  setSelectedPlaybook: (playbook: Playbook | null) => void
  playbookPanelMode: PlaybookPanelMode
  setPlaybookPanelMode: (mode: PlaybookPanelMode) => void
  openCreatePlaybookPanel: () => void
  openViewEditPlaybookPanel: (playbook: Playbook) => void
  closePlaybookPanel: () => void
  // View chats panel
  viewChatsPlaybook: Playbook | null
  setViewChatsPlaybook: (playbook: Playbook | null) => void
  openViewChatsPanel: (playbook: Playbook) => void
  closeViewChatsPanel: () => void
  // Newly created playbook highlight
  newlyCreatedPlaybookId: string | null
  setNewlyCreatedPlaybookId: (id: string | null) => void
}

const initialPlaybookPanelState: PlaybookPanelState = {
  playbookPanelOpen: false,
  setPlaybookPanelOpen: () => {
    return
  },
  selectedPlaybook: null,
  setSelectedPlaybook: () => {
    return
  },
  playbookPanelMode: 'create',
  setPlaybookPanelMode: () => {
    return
  },
  openCreatePlaybookPanel: () => {
    return
  },
  openViewEditPlaybookPanel: () => {
    return
  },
  closePlaybookPanel: () => {
    return
  },
  viewChatsPlaybook: null,
  setViewChatsPlaybook: () => {
    return
  },
  openViewChatsPanel: () => {
    return
  },
  closeViewChatsPanel: () => {
    return
  },
  newlyCreatedPlaybookId: null,
  setNewlyCreatedPlaybookId: () => {
    return
  },
}

const PlaybookPanelContext = createContext<PlaybookPanelState>(
  initialPlaybookPanelState,
)

export const usePlaybookPanelContext = (): PlaybookPanelState =>
  useContext(PlaybookPanelContext)

export const PlaybookPanelProvider = ({
  children,
}: React.PropsWithChildren): JSX.Element => {
  const [playbookPanelOpen, setPlaybookPanelOpen] = useState(false)
  const [selectedPlaybook, setSelectedPlaybook] = useState<Playbook | null>(
    null,
  )
  const [playbookPanelMode, setPlaybookPanelMode] =
    useState<PlaybookPanelMode>('create')
  const [viewChatsPlaybook, setViewChatsPlaybook] = useState<Playbook | null>(
    null,
  )
  const [newlyCreatedPlaybookId, setNewlyCreatedPlaybookId] = useState<
    string | null
  >(null)

  const openCreatePlaybookPanel = useCallback(() => {
    setSelectedPlaybook(null)
    setPlaybookPanelMode('create')
    setPlaybookPanelOpen(true)
  }, [])

  const openViewEditPlaybookPanel = useCallback((playbook: Playbook) => {
    setSelectedPlaybook(playbook)
    // Mode is always 'edit' - the panel will determine view-only based on user permissions
    setPlaybookPanelMode('edit')
    setPlaybookPanelOpen(true)
  }, [])

  const closePlaybookPanel = useCallback(() => {
    setPlaybookPanelOpen(false)
    setSelectedPlaybook(null)
  }, [])

  const openViewChatsPanel = useCallback((playbook: Playbook) => {
    setViewChatsPlaybook(playbook)
  }, [])

  const closeViewChatsPanel = useCallback(() => {
    setViewChatsPlaybook(null)
  }, [])

  return (
    <PlaybookPanelContext.Provider
      value={{
        playbookPanelOpen,
        setPlaybookPanelOpen,
        selectedPlaybook,
        setSelectedPlaybook,
        playbookPanelMode,
        setPlaybookPanelMode,
        openCreatePlaybookPanel,
        openViewEditPlaybookPanel,
        closePlaybookPanel,
        viewChatsPlaybook,
        setViewChatsPlaybook,
        openViewChatsPanel,
        closeViewChatsPanel,
        newlyCreatedPlaybookId,
        setNewlyCreatedPlaybookId,
      }}
    >
      {children}
    </PlaybookPanelContext.Provider>
  )
}
